import type { ComponentType } from 'react';
import type { FieldOption, FieldSchema, FieldType } from '@solote/contracts';

/**
 * REGISTRY DE CAMPOS
 *
 * O motor de formulario nao conhece componentes: ele consulta este registry.
 * Um campo exotico ("planta do loteamento com hotspots") vira um componente
 * registrado por nome, e o painel passa a oferece-lo em `presentation.widget`
 * — o unico ponto onde o desenvolvedor precisa entrar, e ainda assim sem
 * tocar no motor.
 */

export type FieldComponentProps = {
  field: FieldSchema;
  value: unknown;
  onChange: (value: unknown) => void;
  onBlur: () => void;
  error: string | undefined;
  disabled: boolean;
  /** Opcoes ja resolvidas (estatica, taxonomia ou remota). */
  options: FieldOption[];
  /** Valores atuais do formulario — para campos que dependem de outros. */
  formValues: Record<string, unknown>;
  /** Id do input, para associar com o <label>. */
  inputId: string;
};

export type FieldComponent = ComponentType<FieldComponentProps>;

const byType = new Map<string, FieldComponent>();
const byWidget = new Map<string, FieldComponent>();

/** Registra o componente padrao de um ou mais tipos. */
export function registerFieldType(types: FieldType | FieldType[], component: FieldComponent): void {
  for (const type of Array.isArray(types) ? types : [types]) byType.set(type, component);
}

/** Registra um widget nomeado, selecionavel no painel. */
export function registerFieldWidget(name: string, component: FieldComponent): void {
  byWidget.set(name, component);
}

/** Widget nomeado tem prioridade sobre o padrao do tipo. */
export function resolveFieldComponent(field: FieldSchema): FieldComponent | null {
  if (field.presentation.widget) {
    const widget = byWidget.get(field.presentation.widget);
    if (widget) return widget;
    if (import.meta.env.DEV) {
      console.warn(`[forms] widget "${field.presentation.widget}" nao registrado; usando o padrao de "${field.type}".`);
    }
  }
  return byType.get(field.type) ?? null;
}

/** Consumido pelo painel para popular o seletor de widget. */
export function listRegisteredWidgets(): string[] {
  return [...byWidget.keys()].sort();
}
